// Persistent storage + quota accounting (docs/04 §Offline behavior, Sprint 11).
//
// Two safety nets against the WebView evicting our origin storage under pressure:
//  1. Request PERSISTENT storage on entering RUNNING. In a kiosk/installed WebView
//     this is a prompt-less grant; on the desktop it may be a no-op. Either way the
//     result is logged, never awaited into the critical path.
//  2. A best-effort PRE-FLIGHT before caching a new manifest's media: sum the bytes
//     we EXPECT to write (from a per-URL ledger of what past downloads actually
//     cost — the manifest itself carries no byte sizes, see the D3 note in the
//     handoff) and compare against the remaining quota. When it won't fit — or when
//     a `cache.put` later throws QuotaExceededError — the player keeps the old
//     content playing (unchanged behavior) but now RAISES a storage-pressure flag
//     the heartbeat reports, instead of failing silently.
//
// The pre-flight math is a pure function so it unit-tests without a browser; the
// navigator.storage calls are thin wrappers that degrade to nulls where the API
// is unavailable.

/** A point-in-time storage estimate. `quota === null` → unknown (can't judge). */
export interface StorageReport {
  usage: number;
  quota: number | null;
}

/** Per-URL byte ledger: manifest media URL → bytes the cached response occupied. */
export type MediaLedger = Record<string, number>;

/**
 * Ask the browser to make this origin's storage persistent (survive eviction).
 * Prompt-less in kiosk WebView contexts; a no-op elsewhere. Never throws — logs
 * the granted/denied/unsupported outcome and returns it.
 */
export async function requestPersistentStorage(
  log: (code: string, message?: string) => void,
): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) {
      log('storage_persist_unsupported');
      return false;
    }
    const granted = await navigator.storage.persist();
    log('storage_persist', granted ? 'granted' : 'denied');
    return granted;
  } catch (e) {
    log('storage_persist_failed', String(e));
    return false;
  }
}

/** Current {usage, quota} via navigator.storage.estimate(); nulls if unavailable. */
export async function estimateStorage(): Promise<StorageReport> {
  try {
    if (!navigator.storage?.estimate) return { usage: 0, quota: null };
    const est = await navigator.storage.estimate();
    return { usage: est.usage ?? 0, quota: est.quota ?? null };
  } catch {
    return { usage: 0, quota: null };
  }
}

export interface Preflight {
  /** Bytes we can predict from the ledger (URLs never cached before are unknown). */
  knownBytes: number;
  /** How many of `toDownload` had no ledger entry (prediction is a lower bound). */
  unknownCount: number;
  /** Remaining quota headroom, or null when the quota is unknown. */
  available: number | null;
  /**
   * Whether the KNOWN bytes fit the available headroom. `true` when the quota is
   * unknown (we can't refuse on a guess) — the real QuotaExceededError still
   * catches an over-fill at write time.
   */
  fits: boolean;
}

/**
 * Pure pre-flight: given the URLs about to be downloaded, the byte ledger, and a
 * storage estimate, predict whether the (knowable) new bytes fit. A conservative
 * lower bound — unknown URLs contribute 0 — so it only ever flags pressure it is
 * sure of; the write-time QuotaExceededError is the backstop for the rest.
 */
export function preflight(
  toDownload: readonly string[],
  ledger: MediaLedger,
  report: StorageReport,
): Preflight {
  let knownBytes = 0;
  let unknownCount = 0;
  for (const url of toDownload) {
    const bytes = ledger[url];
    if (typeof bytes === 'number' && bytes >= 0) knownBytes += bytes;
    else unknownCount++;
  }
  const available = report.quota === null ? null : Math.max(0, report.quota - report.usage);
  const fits = available === null ? true : knownBytes <= available;
  return { knownBytes, unknownCount, available, fits };
}

/** Format the `X-Storage-Usage` heartbeat header value: `<usedBytes>/<quotaBytes>`. */
export function formatStorageUsage(report: StorageReport): string {
  return `${report.usage}/${report.quota ?? 0}`;
}
